import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '@/reducers/types';
import { getNotifications } from '@/reducers/users/users.service';
import { useUnreadCounts } from '@/components/myConversations/api/unread/useUnreadCounts';
import { FEATURE_FLAGS } from '@/components/myConversations/featureFlags';

export interface RailBadgeCounts {
  messages: number;
  notifications: number;
}

/**
 * Owns the app-wide unread badge counts shown on the rail.
 *
 * Messages: derived from the new messaging `unread-counts` React Query cache
 *   (useUnreadCounts). socket/global.js keeps that cache live app-wide (it
 *   patches the cache on every message socket event, even when the messages
 *   page is unmounted), so here we only read + sum the per-channel map.
 *
 * Notifications: the legacy topbar bell used to own this via useNotification.
 *   With the topbar gone, nothing fetched the count on landing (it sat at 0).
 *   This hook restores global ownership: it fetches the initial unread count
 *   once on mount and increments on FRONT_NOTIFICATION socket events. The
 *   /notifications panel no longer dispatches the count (see useNotification),
 *   so there is a single global owner and no double-counting.
 */
export const useRailBadges = (): RailBadgeCounts => {
  const dispatch = useDispatch();

  const { userId, notificationCount, socket } = useSelector(
    (store: RootState) => ({
      userId: store.loggedUser?.data?.id,
      notificationCount: store.notification.NotificationCount,
      socket: store.socket.container,
    })
  );

  // --- Messages: sum the per-channel unread map (kept live by socket/global.js) ---
  const { data: unreadCounts = {} } = useUnreadCounts({
    config: { enabled: FEATURE_FLAGS.MESSAGING_ENABLED },
  });
  const messages = useMemo(
    () =>
      Object.values(unreadCounts as Record<string, number>).reduce(
        (sum, n) => sum + (n || 0),
        0
      ),
    [unreadCounts]
  );

  // --- Notifications: initial global fetch (was the removed topbar bell's job) ---
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      // location: null === TABS.ALL.filterKey — unread count across all notifications.
      const response = await getNotifications(userId, {
        page: 1,
        limit: 1,
        location: null,
        read: 0,
      });
      if (!cancelled && response && response.data) {
        dispatch({
          type: 'SET_NOTIFICATION_COUNT',
          count: response.data.totalCount,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, dispatch]);

  // --- Notifications: live increments app-wide (replaces the bell's listener) ---
  useEffect(() => {
    if (!socket || !userId) return undefined;
    const onNewNotification = (newNotification: { userId?: number }) => {
      if (newNotification?.userId !== userId) return;
      dispatch({ type: 'UPDATE_NOTIFICATION_COUNT', count: 1 });
    };
    socket.on('FRONT_NOTIFICATION', onNewNotification);
    return () => {
      socket.off('FRONT_NOTIFICATION', onNewNotification);
    };
  }, [socket, userId, dispatch]);

  return { messages, notifications: notificationCount };
};
